import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { JSONContent } from '@tiptap/core';
import type { PolicyAcknowledgementBlock } from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { DocumentIcon } from './contentBlockIcons';
import { createContentBlockStyles } from './styles/contentBlocks';
import { getTitleTypography } from './styles/titleTypography';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { InteractionMessage } from './InteractionMessage';
import {
  createRichTextContentResetStyles,
  createRichTextRendererExtensions,
  isSafeExternalUrl,
  safeGenerateRichTextHTML,
} from './richTextRenderer';
import { RichTextContent } from './RichTextContent';

const descriptionResetStyles = createRichTextContentResetStyles(
  'policy-description-rich',
  '0.6em'
);

export interface PolicyAcknowledgementRendererProps {
  block: PolicyAcknowledgementBlock;
  onInteractionComplete?: () => void;
  onAcknowledged?: () => void;
  /**
   * NA-319 relaunch/session-nav restore. Policy Acknowledgement has no
   * persisted per-learner response (no backend support at all), but when
   * present the checkbox mounts directly into its checked/locked state
   * instead of resetting to unchecked. Supplied by `CourseCanvas.tsx` from
   * either the learner's real, actually-checked state earlier in this
   * browser session, or — when the lesson containing this block is already
   * confirmed complete and this specific block instance requires
   * interaction — a guaranteed-checked fallback, since the lesson could not
   * have completed without this checkbox being checked.
   */
  initialAcknowledged?: boolean;
}

export function PolicyAcknowledgementRenderer({
  block,
  onInteractionComplete,
  onAcknowledged,
  initialAcknowledged,
}: PolicyAcknowledgementRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const richTextExtensions = useMemo(
    () => createRichTextRendererExtensions(),
    []
  );

  // NA-227: honor the block's chosen heading level (editor already respects it);
  // the renderer previously hardcoded an <h2>/40px title regardless of titleLevel.
  const titleLevel = block.titleLevel ?? 'h2';
  const TitleTag = titleLevel;
  const titleTypography = getTitleTypography(tokens, titleLevel, { isMobile });
  const policyTitleStyle: React.CSSProperties = {
    ...styles.policyTitle,
    fontSize: titleTypography.size,
    fontWeight: titleTypography.weight,
    lineHeight: titleTypography.lineHeight,
  };

  const [hasViewedDocument, setHasViewedDocument] = useState(
    () => initialAcknowledged === true
  );
  const [isAcknowledged, setIsAcknowledged] = useState(
    () => initialAcknowledged === true
  );
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [showNoPolicyMessage, setShowNoPolicyMessage] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  // NA-319: `initialAcknowledged` can resolve asynchronously after this
  // block's first mount (same-session data is available immediately, but
  // the lesson-completion fallback depends on completion-status tracking
  // that may settle after first paint). The `!isAcknowledged` guard avoids
  // clobbering an in-progress or already-locked state. `hasViewedDocument`
  // is seeded alongside `isAcknowledged` (not just the acknowledged message)
  // so the checkbox row itself renders in its checked/disabled state on
  // restore, matching what the learner actually saw right after checking it
  // live — the live flow can only reach `isAcknowledged: true` by first
  // viewing the document, so restoring one without the other would be an
  // inconsistent view the learner never actually had.
  useEffect(() => {
    if (initialAcknowledged && !isAcknowledged) {
      setHasViewedDocument(true);
      setIsAcknowledged(true);
    }
  }, [initialAcknowledged, isAcknowledged]);

  const requireInteraction = block.requireInteraction ?? false;

  const isRichDescription = typeof block.description !== 'string';
  const descriptionHtml = useMemo(() => {
    if (typeof block.description === 'string') {
      return '';
    }
    return safeGenerateRichTextHTML(
      block.description as JSONContent,
      richTextExtensions
    );
  }, [block.description, richTextExtensions]);

  const handleViewDocument = useCallback(() => {
    if (!block.policyUrl || !isSafeExternalUrl(block.policyUrl)) {
      setShowNoPolicyMessage(true);
      setHasViewedDocument(true);
      return;
    }

    if (block.policyType === 'link') {
      window.open(block.policyUrl, '_blank', 'noopener,noreferrer,width=800,height=600');
      setHasViewedDocument(true);
      return;
    }

    // PDF: if it contains links, open in a new window so they are clickable.
    // Legacy blocks with undefined pdfHasLinks default to new-window for safety.
    // Window features (width/height) force a popup window rather than a new tab.
    const pdfHasLinks = block.pdfHasLinks ?? true;
    if (pdfHasLinks) {
      window.open(
        block.policyUrl,
        '_blank',
        'noopener,noreferrer,width=1000,height=800'
      );
      setHasViewedDocument(true);
    } else {
      setIsPdfModalOpen(true);
    }
  }, [block.policyType, block.policyUrl, block.pdfHasLinks]);

  const handleCloseNoPolicyMessage = useCallback(() => {
    setShowNoPolicyMessage(false);
  }, []);

  const handleClosePdfModal = useCallback(() => {
    setIsPdfModalOpen(false);
    setHasViewedDocument(true);
  }, []);

  const handleAcknowledge = useCallback(() => {
    setIsAcknowledged(true);
    onAcknowledged?.();
    onInteractionComplete?.();
  }, [onAcknowledged, onInteractionComplete]);

  useEffect(() => {
    if (!showNoPolicyMessage && !isPdfModalOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isPdfModalOpen) {
        handleClosePdfModal();
      } else if (showNoPolicyMessage) {
        handleCloseNoPolicyMessage();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    showNoPolicyMessage,
    isPdfModalOpen,
    handleCloseNoPolicyMessage,
    handleClosePdfModal,
  ]);

  const viewButtonStyle = useMemo(
    () =>
      isButtonHovered
        ? { ...styles.policyViewButton, ...styles.policyViewButtonHover }
        : styles.policyViewButton,
    [isButtonHovered, styles]
  );

  return (
    <div style={styles.policyContainer}>
      <TitleTag style={policyTitleStyle}>{block.title}</TitleTag>

      {isRichDescription ? (
        <>
          <style>{descriptionResetStyles}</style>
          <RichTextContent
            className="policy-description-rich"
            style={styles.policyDescription}
            html={descriptionHtml}
          />
        </>
      ) : (
        <p style={styles.policyDescription}>
          {block.description as string}
        </p>
      )}

      <div style={styles.policyButtonRow}>
        <button
          type="button"
          style={viewButtonStyle}
          onClick={handleViewDocument}
          onMouseEnter={() => setIsButtonHovered(true)}
          onMouseLeave={() => setIsButtonHovered(false)}
          aria-label={t('policy.view_document')}
        >
          <DocumentIcon style={styles.policyViewButtonIcon} />
          {t('policy.view_document')}
        </button>

        {requireInteraction && hasViewedDocument && (
          <label style={styles.policyCheckboxWrapper}>
            <input
              type="checkbox"
              style={{
                ...styles.policyCheckboxInput,
                ...(isAcknowledged ? { cursor: 'default' } : {}),
              }}
              checked={isAcknowledged}
              disabled={isAcknowledged}
              onChange={handleAcknowledge}
              aria-label={t('policy.acknowledge')}
            />
            <span style={styles.policyCheckboxLabel}>
              {t('policy.acknowledge')}
            </span>
          </label>
        )}
      </div>

      {requireInteraction && !isAcknowledged && (
        <InteractionMessage
          variant="boxed"
          topOffset={50}
          requireInteraction={requireInteraction}
          isComplete={false}
          message={block.interactionMessage}
          fallbackMessage={t('policy.review_to_continue')}
        />
      )}
      {requireInteraction && isAcknowledged && (
        <div style={{ marginTop: '60px' }}>
          <p style={styles.policyAcknowledgedMessage}>
            {t('policy.acknowledged')}
          </p>
        </div>
      )}

      {isPdfModalOpen && createPortal(
        <div
          style={styles.policyModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={t('policy.viewer')}
          onClick={handleClosePdfModal}
        >
          <div
            style={styles.policyModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.policyModalHeader}>
              <span style={styles.policyModalHeaderTitle}>
                {block.title || t('policy.document')}
              </span>
            </div>
            <button
              type="button"
              style={styles.policyModalCloseButton}
              onClick={handleClosePdfModal}
              aria-label={t('policy.close_viewer')}
            >
              ×
            </button>
            <iframe
              src={block.policyUrl}
              referrerPolicy="no-referrer"
              style={styles.policyModalIframe}
              title={t('policy.document')}
            />
            <div style={styles.policyModalFooter}>
              <button
                type="button"
                style={styles.policyModalDoneButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClosePdfModal();
                }}
              >
                {t('policy.done_reviewing')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showNoPolicyMessage && createPortal(
        <div
          style={styles.policyModalOverlay}
          role="dialog"
          aria-modal="true"
          aria-label={t('policy.unavailable')}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              ...styles.policyModalContent,
              height: 'auto',
              maxWidth: '500px',
              padding: '0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.policyModalHeader}>
              <span style={styles.policyModalHeaderTitle}>
                {t('policy.unavailable')}
              </span>
            </div>
            <div style={{
              padding: '32px 24px',
              textAlign: 'center' as const,
              fontSize: tokens.typography.body.size,
              fontFamily: tokens.typography.fontFamily,
              lineHeight: tokens.typography.body.lineHeight,
              color: tokens.text.primary,
            }}>
              {t('policy.unavailable_message')}
            </div>
            <div style={styles.policyModalFooter}>
              <button
                type="button"
                style={styles.policyModalDoneButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseNoPolicyMessage();
                }}
              >
                {t('ask_ema.aria_close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

PolicyAcknowledgementRenderer.displayName = 'PolicyAcknowledgementRenderer';
